import React, { useState } from 'react';
import { supabase } from './supabaseClient';
import { useCompany, PLAN_LABELS } from './CompanyContext';
import { logAuthEvent } from './auditLog';
import './Settings.css';

const SUBSCRIPTION_STATUS_LABELS: Record<string, string> = {
  trial:        'Prueba gratuita',
  pending:      'Pendiente de pago',
  active:       'Activa',
  overdue:      'Vencida',
  grace_period: 'Período de gracia',
  suspended:    'Suspendida',
  cancelled:    'Cancelada',
};

const BILLING_CYCLE_LABELS: Record<string, string> = {
  monthly: 'Mensual',
  annual:  'Anual',
};

export default function Settings() {
  const { company, profile, refresh } = useCompany();
  const [saving, setSaving] = useState(false);
  const [saved, setSaved]   = useState(false);

  const [newPassword,     setNewPassword]     = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [pwdError,        setPwdError]        = useState('');
  const [pwdSuccess,      setPwdSuccess]      = useState(false);
  const [pwdSaving,       setPwdSaving]       = useState(false);

  const [notifSaving, setNotifSaving] = useState(false);
  const [notifSaved,  setNotifSaved]  = useState(false);

  if (!company || !profile) return null;
  if (profile.role !== 'admin') return (
    <div style={{ padding: '40px 0', textAlign: 'center', color: '#475569' }}>
      Solo los administradores pueden acceder a la configuración.
    </div>
  );

  async function toggleEmergencyAccess() {
    if (saving) return;
    setSaving(true);
    setSaved(false);
    const next = !company!.emergency_access_enabled;
    const { error } = await supabase
      .from('companies')
      .update({ emergency_access_enabled: next })
      .eq('id', company!.id);
    if (!error) {
      await refresh();
      setSaved(true);
      setTimeout(() => setSaved(false), 2500);
    }
    setSaving(false);
  }

  async function handleChangePassword(e: React.FormEvent) {
    e.preventDefault();
    setPwdError('');
    setPwdSuccess(false);
    if (newPassword.length < 8) {
      setPwdError('La contraseña debe tener al menos 8 caracteres.');
      return;
    }
    if (!/[A-Z]/.test(newPassword)) {
      setPwdError('La contraseña debe tener al menos una letra mayúscula.');
      return;
    }
    if (!/[0-9]/.test(newPassword)) {
      setPwdError('La contraseña debe tener al menos un número.');
      return;
    }
    if (newPassword !== confirmPassword) {
      setPwdError('Las contraseñas no coinciden.');
      return;
    }
    setPwdSaving(true);
    const { error } = await supabase.auth.updateUser({ password: newPassword });
    setPwdSaving(false);
    if (error) {
      setPwdError('No se pudo actualizar la contraseña. Intenta nuevamente.');
      return;
    }
    logAuthEvent('password_changed');
    setPwdSuccess(true);
    setNewPassword('');
    setConfirmPassword('');
  }

  async function toggleEmailNotifications() {
    if (notifSaving) return;
    setNotifSaving(true);
    setNotifSaved(false);
    const next = !company!.email_notifications;
    const { error } = await supabase
      .from('companies')
      .update({ email_notifications: next })
      .eq('id', company!.id);
    if (!error) {
      await refresh();
      setNotifSaved(true);
      setTimeout(() => setNotifSaved(false), 2500);
    }
    setNotifSaving(false);
  }

  return (
    <div className="settings-root">

      {/* ── Empresa ── */}
      <section className="settings-section">
        <h3 className="settings-section-title">Información de la empresa</h3>
        <div className="settings-card">
          <div className="settings-row">
            <span className="settings-label">Nombre</span>
            <span className="settings-value">{company.name}</span>
          </div>
          {company.ruc && (
            <div className="settings-row">
              <span className="settings-label">RUC / NIT</span>
              <span className="settings-value">{company.ruc}</span>
            </div>
          )}
          {company.country && (
            <div className="settings-row">
              <span className="settings-label">País</span>
              <span className="settings-value">{company.country}</span>
            </div>
          )}
        </div>
      </section>

      {/* ── Privacidad y Accesos ── */}
      <section className="settings-section">
        <h3 className="settings-section-title">Privacidad y Accesos</h3>

        <div className="settings-card">
          <div className="settings-toggle-row">
            <div className="settings-toggle-info">
              <span className="settings-toggle-title">
                <EmergencyIcon />
                Acceso para servicios de emergencia
              </span>
              <span className="settings-toggle-desc">
                Permite que entidades de seguridad pública autorizadas accedan a la información de
                sus bodegas con fines de emergencia e inspección técnica. Esta autorización es
                voluntaria y revocable en cualquier momento (ver cláusula 10 de los Términos).
              </span>
            </div>
            <button
              className={`settings-toggle ${company.emergency_access_enabled ? 'on' : 'off'}`}
              onClick={toggleEmergencyAccess}
              disabled={saving}
              aria-label="Alternar acceso de emergencia"
              title={company.emergency_access_enabled ? 'Desactivar' : 'Activar'}
            >
              <span className="settings-toggle-knob" />
            </button>
          </div>

          {company.emergency_access_enabled && (
            <div className="settings-access-active">
              <ShieldCheckIcon />
              <span>
                Acceso activo — las entidades autorizadas pueden consultar sus bodegas y
                zonas en caso de emergencia o inspección técnica.
              </span>
            </div>
          )}

          {saved && (
            <p className="settings-saved-msg">✓ Configuración guardada</p>
          )}
        </div>
      </section>

      {/* ── Cambiar contraseña ── */}
      <section className="settings-section">
        <h3 className="settings-section-title">Cambiar contraseña</h3>
        <div className="settings-card">
          <form className="settings-form" onSubmit={handleChangePassword}>
            <div className="settings-field">
              <label htmlFor="settings-new-pwd">Nueva contraseña</label>
              <input
                id="settings-new-pwd"
                type="password"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                placeholder="Mínimo 8 caracteres, 1 mayúscula, 1 número"
                disabled={pwdSaving}
              />
            </div>
            <div className="settings-field">
              <label htmlFor="settings-confirm-pwd">Confirmar contraseña</label>
              <input
                id="settings-confirm-pwd"
                type="password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                placeholder="Repite la nueva contraseña"
                disabled={pwdSaving}
              />
            </div>

            {pwdError && <p className="settings-error-msg">{pwdError}</p>}
            {pwdSuccess && <p className="settings-saved-msg">✓ Contraseña actualizada</p>}

            <button type="submit" className="settings-btn-primary" disabled={pwdSaving}>
              {pwdSaving ? 'Actualizando…' : 'Actualizar contraseña'}
            </button>
          </form>
        </div>
      </section>

      {/* ── Notificaciones ── */}
      <section className="settings-section">
        <h3 className="settings-section-title">Notificaciones</h3>
        <div className="settings-card">
          <div className="settings-toggle-row">
            <div className="settings-toggle-info">
              <span className="settings-toggle-title">
                <BellIcon />
                Recibir alertas por email
              </span>
              <span className="settings-toggle-desc">
                Recibe notificaciones por correo sobre vencimientos, incidentes y alertas
                importantes de tu empresa.
              </span>
            </div>
            <button
              className={`settings-toggle ${company.email_notifications ? 'on' : 'off'}`}
              onClick={toggleEmailNotifications}
              disabled={notifSaving}
              aria-label="Alternar notificaciones por email"
              title={company.email_notifications ? 'Desactivar' : 'Activar'}
            >
              <span className="settings-toggle-knob" />
            </button>
          </div>

          {notifSaved && (
            <p className="settings-saved-msg">✓ Configuración guardada</p>
          )}
        </div>
      </section>

      {/* ── Facturación ── */}
      {profile.role === 'admin' && (
        <section className="settings-section">
          <h3 className="settings-section-title">Facturación</h3>
          <div className="settings-card">
            <div className="settings-row">
              <span className="settings-label">Plan actual</span>
              <span className="settings-value">{PLAN_LABELS[company.plan] ?? company.plan}</span>
            </div>
            <div className="settings-row">
              <span className="settings-label">Ciclo de facturación</span>
              <span className="settings-value">
                {company.billing_cycle ? (BILLING_CYCLE_LABELS[company.billing_cycle] ?? company.billing_cycle) : '—'}
              </span>
            </div>
            <div className="settings-row">
              <span className="settings-label">Próxima fecha de cobro</span>
              <span className="settings-value">
                {company.current_period_end
                  ? new Date(company.current_period_end).toLocaleDateString('es-EC', { day: 'numeric', month: 'long', year: 'numeric' })
                  : '—'}
              </span>
            </div>
            <div className="settings-row">
              <span className="settings-label">Estado de suscripción</span>
              <span className="settings-value">
                {company.subscription_status
                  ? (SUBSCRIPTION_STATUS_LABELS[company.subscription_status] ?? company.subscription_status)
                  : '—'}
              </span>
            </div>
            {company.subscription_status !== 'trial' && (
              <div className="settings-row">
                <span className="settings-label">RUC registrado</span>
                <span className="settings-value">{company.ruc ?? '—'}</span>
              </div>
            )}
            <div style={{ padding: '16px 20px' }}>
              <a href="/pricing" className="settings-btn-primary settings-btn-link">Ver planes</a>
            </div>
          </div>
        </section>
      )}

    </div>
  );
}

function BellIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0 }}>
      <path d="M18 8a6 6 0 00-12 0c0 7-3 9-3 9h18s-3-2-3-9" />
      <path d="M13.73 21a2 2 0 01-3.46 0" />
    </svg>
  );
}

function EmergencyIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0 }}>
      <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
    </svg>
  );
}

function ShieldCheckIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0, color: '#22c55e' }}>
      <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
      <polyline points="9 12 11 14 15 10" />
    </svg>
  );
}
